import {
  ConversationMessageErrorInfo,
  ConversationMessageMetadata,
  LLMModels,
  Nullable,
} from "@shaarp/common";
import { UIMessage } from "ai";

type BuildChatErrorMessageParams = {
  error: Error;
  llmId: LLMModels;
  userId: Nullable<string>;
  retryable: boolean;
};

type ParsedErrorBody = {
  message?: unknown;
  error?: unknown;
  code?: unknown;
  filenames?: unknown;
};

export function buildChatErrorMessage({
  error,
  llmId,
  userId,
  retryable,
}: BuildChatErrorMessageParams): UIMessage<ConversationMessageMetadata> {
  const errorInfo = parseChatError(error, retryable);

  return {
    id: `local-error-${Date.now()}`,
    role: "assistant",
    parts: [
      {
        type: "text",
        text: errorInfo.message,
        state: "done",
      },
    ],
    metadata: {
      createdAt: new Date(),
      llmId,
      userId,
      files: [],
      error: errorInfo,
    },
  };
}

/**
 * The notice for a turn that disappeared: the connection dropped, nothing was
 * left running on the server, and the database holds no answer.
 *
 * Reuses `llm_provider_error`, whose title and explanation the thread already
 * translates, so the reader gets the same card as any other failed answer.
 *
 * Never retryable: the question is already stored, and the server keeps no
 * trace of a replay, so sending it again puts it in the thread — and in what
 * the model reads — twice.
 */
export function buildAnswerLostErrorMessage({
  llmId,
  userId,
}: Omit<BuildChatErrorMessageParams, "error" | "retryable">): UIMessage<ConversationMessageMetadata> {
  return buildChatErrorMessage({
    error: new Error(
      JSON.stringify({
        code: "llm_provider_error",
        message: "The connection was lost before the answer was complete.",
      }),
    ),
    llmId,
    userId,
    retryable: false,
  });
}

/**
 * Error codes that a retry can never fix: the same request would rebuild the
 * same oversized context and fail again. Offering "Retry" on those only sends
 * the user in circles, so the button is hidden and the dedicated explanation
 * tells them what to do instead.
 */
const NON_RETRYABLE_ERROR_CODES = new Set([
  "context_budget_exceeded",
  // Re-sending the same oversized document would fail identically.
  "document_too_large",
  // A locked team is refused again until it is unblocked.
  "TEAM_BLOCKED",
]);

function parseChatError(
  error: Error,
  retryable: boolean,
): ConversationMessageErrorInfo {
  const rawMessage = error.message.trim();
  const parsed = parseJsonErrorBody(rawMessage);
  const parsedMessage = normalizeMessage(parsed?.message ?? parsed?.error);
  const message = parsedMessage || rawMessage || error.name;
  const code = normalizeMessage(parsed?.code);
  const cause = code || getErrorCause(error);
  // Named files make the difference between "a document is too large" and
  // knowing which one to replace.
  const filenames = Array.isArray(parsed?.filenames)
    ? parsed.filenames.filter((name): name is string => typeof name === "string")
    : [];

  return {
    message,
    ...(cause ? { cause } : {}),
    ...(code ? { code } : {}),
    ...(filenames.length > 0 ? { filenames } : {}),
    retryable: retryable && !(code && NON_RETRYABLE_ERROR_CODES.has(code)),
  };
}

function getErrorCause(error: Error): string | null {
  const cause = (error as Error & { cause?: unknown }).cause;
  if (typeof cause === "string" && cause.trim()) {
    return cause.trim();
  }
  if (cause instanceof Error && cause.message.trim()) {
    return cause.message.trim();
  }
  return null;
}

function parseJsonErrorBody(rawMessage: string): ParsedErrorBody | null {
  try {
    const parsed = JSON.parse(rawMessage) as unknown;
    if (parsed && typeof parsed === "object") {
      return parsed as ParsedErrorBody;
    }
  } catch {
    return null;
  }

  return null;
}

function normalizeMessage(value: unknown): string | null {
  if (typeof value === "string" && value.trim()) {
    return value.trim();
  }
  if (Array.isArray(value)) {
    const joined = value.filter((item) => typeof item === "string").join(", ");
    return joined.trim() || null;
  }
  return null;
}
